import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { BookOpen, Check, Coins, Sparkles, ArrowRight, CreditCard, ShieldCheck, Crown, School, Users, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import LanguageSwitcher from "../components/LanguageSwitcher";

// Default fallbacks (will be overridden by API data)
const DEFAULT_PACKAGES = {
  starter: { credits: 100, price: 4.99, name: "Starter Pack", popular: false },
  pro: { credits: 250, price: 9.99, name: "Pro Pack", popular: true },
  school: { credits: 700, price: 24.99, name: "School Pack", popular: false }
};

const CREDIT_COST_LABELS = {
  book_analysis: "pricing.bookAnalysis",
  test_generation: "pricing.testGeneration",
  quiz_generation: "pricing.quizGeneration",
  download_test: "pricing.testDownload",
  download_quiz: "pricing.quizDownload",
  lecture_notes: "pricing.lectureNotes",
  download_notes: "pricing.notesDownload"
};

// Keys to hide from pricing display
const HIDDEN_COST_KEYS = [];

const PricingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(null);
  const [showPayPal, setShowPayPal] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(false);
  
  // Dynamic pricing data from API
  const [packages, setPackages] = useState(DEFAULT_PACKAGES);
  const [creditCosts, setCreditCosts] = useState({});
  const [pricingLoading, setPricingLoading] = useState(true);
  
  // PayPal config from backend
  const [paypalConfig, setPaypalConfig] = useState({ client_id: "", mode: "sandbox" });

  useEffect(() => {
    fetchPricingData();
    if (user) {
      fetchSubscriptionStatus();
    }
  }, [user]);

  const fetchPricingData = async () => {
    try {
      const [packagesRes, costsRes, paypalRes] = await Promise.all([
        axios.get(`${API}/credits/packages`),
        axios.get(`${API}/credits/costs`),
        axios.get(`${API}/payments/paypal/config`)
      ]);
      setPackages(packagesRes.data);
      setCreditCosts(costsRes.data);
      setPaypalConfig(paypalRes.data);
    } catch (error) {
      console.error("Failed to fetch pricing data:", error);
      // Keep defaults on error
    } finally {
      setPricingLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeaders(token)
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      // Ignore
    }
  };

  const handlePremiumSubscription = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "teacher") {
      toast.error("Premium subscription is only for teachers");
      return;
    }
    setPremiumLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/premium`, 
        { origin_url: window.location.origin },
        { headers: getAuthHeaders(token) }
      );
      window.location.href = response.data.url;
    } catch (error) {
      // Handle validation errors (422) which return an array of error objects
      const detail = error.response?.data?.detail;
      let errorMessage = "Failed to start subscription";
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setPremiumLoading(false);
    }
  };

  const handleStripeCheckout = async (packageId) => {
    if (!user) {
      navigate("/login");
      return;
    }

    setLoading(packageId);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        payment_method: "stripe",
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoading(null);
    }
  };

  const handlePayPalSuccess = async (packageId, orderId) => {
    try {
      await axios.post(`${API}/payments/paypal/complete`, {
        order_id: orderId,
        package_id: packageId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await refreshUser();
      toast.success(`${packages[packageId]?.credits || 0} credits added to your account!`);
      setShowPayPal(null);
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to complete payment");
    }
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="container-main py-4 sm:py-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#4F46E5] to-[#14B8A6] rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              BookTest<span className="text-[#4F46E5]">.pro</span>
            </span>
          </Link>
          
          {user ? (
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher variant="compact" />
              <div className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-full border border-slate-200">
                <Coins className="w-3 h-3 sm:w-4 sm:h-4 text-[#F97316]" />
                <span className="font-semibold text-sm sm:text-base">{user.credits}</span>
                <span className="text-slate-500 text-xs sm:text-sm hidden sm:inline">{t('common.credits')}</span>
              </div>
              <Button onClick={() => navigate("/dashboard")} className="btn-primary text-sm sm:text-base px-3 sm:px-4">
                {t('nav.dashboard')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher variant="compact" />
              <Link to="/login">
                <Button variant="ghost" className="text-sm sm:text-base px-2 sm:px-4">{t('nav.login')}</Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary text-sm sm:text-base px-3 sm:px-4">{t('nav.register')}</Button>
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="container-main py-16" data-testid="pricing-page">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Credit Costs Info */}
        <div className="max-w-3xl mx-auto mb-12">
          <Card className="card-glass">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Coins className="w-5 h-5 text-[#F97316]" />
                {t('pricing.creditCosts')}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">{t('common.action') || 'Action'}</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">{t('common.credits')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingLoading ? (
                      <tr>
                        <td colSpan={2} className="py-4 text-center">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                        </td>
                      </tr>
                    ) : (
                      Object.entries(creditCosts)
                        .filter(([key]) => !HIDDEN_COST_KEYS.includes(key))
                        .map(([key, cost], idx) => (
                        <tr key={key} className={idx % 2 === 0 ? 'bg-white/50' : 'bg-slate-50/50'}>
                          <td className="py-3 px-4 text-slate-700">{t(CREDIT_COST_LABELS[key]) || key.replace(/_/g, ' ')}</td>
                          <td className="py-3 px-4 text-right">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#4F46E5]/10 text-[#4F46E5] font-semibold rounded-full text-sm">
                              {cost}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-4 text-center">
                {t('pricing.freeCreditsNote') || 'New users receive 30 free credits upon registration'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pricingLoading ? (
            <div className="col-span-3 text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
              <p className="text-slate-500 mt-2">{t('common.loading')}</p>
            </div>
          ) : (
            Object.entries(packages).map(([id, pkg]) => (
            <Card 
              key={id}
              className={`card-base relative overflow-hidden ${
                pkg.popular ? 'ring-2 ring-[#4F46E5] scale-105' : ''
              }`}
              data-testid={`package-${id}`}
            >
              {pkg.popular && (
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-[#4F46E5] text-white text-xs font-bold rounded-full">
                    {t('pricing.popular')}
                  </span>
                </div>
              )}
              
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pkg.name}
                </CardTitle>
                <CardDescription>
                  <span className="text-4xl font-bold text-slate-900">${pkg.price}</span>
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-[#F97316]" />
                  <span className="font-semibold text-slate-900">{pkg.credits} Credits</span>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-600">
                    <Check className="w-5 h-5 text-green-500" />
                    {Math.floor(pkg.credits / (creditCosts.book_analysis || 15))} book analyses
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <Check className="w-5 h-5 text-green-500" />
                    {Math.floor(pkg.credits / (creditCosts.test_generation || 5)) * 10} test questions
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <Check className="w-5 h-5 text-green-500" />
                    {Math.floor(pkg.credits / (creditCosts.quiz_generation || 3)) * 10} quiz questions
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <Check className="w-5 h-5 text-green-500" />
                    Unlimited downloads
                  </li>
                </ul>

                <div className="space-y-3 pt-4">
                  <Button
                    onClick={() => handleStripeCheckout(id)}
                    disabled={loading === id}
                    className={`w-full h-12 ${pkg.popular ? 'btn-accent' : 'btn-primary'}`}
                    data-testid={`buy-${id}-stripe`}
                  >
                    {loading === id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay with Card
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => setShowPayPal(showPayPal === id ? null : id)}
                    className="w-full h-12"
                    data-testid={`buy-${id}-paypal`}
                  >
                    Pay with PayPal
                  </Button>
                  
                  {showPayPal === id && paypalConfig.client_id && (
                    <div className="pt-2">
                      <PayPalScriptProvider 
                        key={paypalConfig.client_id}
                        options={{ 
                          clientId: paypalConfig.client_id,
                          currency: "USD"
                        }}
                      >
                        <PayPalButtons
                          style={{ layout: "vertical", height: 40 }}
                          createOrder={(data, actions) => {
                            return actions.order.create({
                              purchase_units: [{
                                amount: { value: pkg.price.toString() }
                              }]
                            });
                          }}
                          onApprove={(data, actions) => {
                            return actions.order.capture().then(() => {
                              handlePayPalSuccess(id, data.orderID);
                            });
                          }}
                          onError={(err) => {
                            toast.error("PayPal payment failed");
                            console.error(err);
                          }}
                        />
                      </PayPalScriptProvider>
                    </div>
                  )}
                  {showPayPal === id && !paypalConfig.client_id && (
                    <div className="pt-2 text-center text-red-500 text-sm">
                      PayPal is not configured. Please contact support.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
          )}
        </div>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-8 mt-12 text-slate-500 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm">Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span className="text-sm">Instant Credit Delivery</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm">No Subscription Required</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-16">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Powerful Features for Education
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to create, share, and track educational content
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Multi-language Support */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Multi-Language Support</h3>
                <p className="text-slate-600 text-sm">
                  Upload content in <strong>any language</strong> - French, Spanish, Greek, Arabic, Chinese, and more. 
                  Our AI generates tests and quizzes in the same language as your source material.
                </p>
              </CardContent>
            </Card>

            {/* Classroom Feature */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#4F46E5] to-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <School className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Classroom Management</h3>
                <p className="text-slate-600 text-sm">
                  <strong>Premium teachers</strong> can create classrooms, generate unique join codes, 
                  and invite students via shareable links. Share tests instantly with your class.
                </p>
              </CardContent>
            </Card>

            {/* Score Tracking */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Leaderboards & Tracking</h3>
                <p className="text-slate-600 text-sm">
                  Track student progress with <strong>real-time leaderboards</strong>. 
                  See individual and class-wide performance to identify areas for improvement.
                </p>
              </CardContent>
            </Card>

            {/* PDF Downloads */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">PDF Downloads</h3>
                <p className="text-slate-600 text-sm">
                  Download tests and quiz results as <strong>professionally formatted PDFs</strong> 
                  with answer keys and explanations. Perfect for printing or sharing.
                </p>
              </CardContent>
            </Card>

            {/* AI-Powered */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#14B8A6] to-green-500 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">AI-Powered Analysis</h3>
                <p className="text-slate-600 text-sm">
                  Our advanced AI <strong>automatically extracts chapters</strong> from any content 
                  and generates relevant questions tailored to your difficulty preferences.
                </p>
              </CardContent>
            </Card>

            {/* Content Formats */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Multiple Input Formats</h3>
                <p className="text-slate-600 text-sm">
                  Upload <strong>PDF files, paste URLs, or enter text</strong> directly. 
                  Works with textbooks, articles, study guides, and any educational material.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Premium Subscription for Teachers */}
      {(!user || user.role === "teacher") && (
        <section className="bg-white py-16">
          <div className="container-main">
            <div className="max-w-3xl mx-auto">
              <Card className="card-base overflow-hidden bg-gradient-to-r from-yellow-50 via-orange-50 to-yellow-50 border-2 border-yellow-300">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <Crown className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                        <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Premium Teacher Plan
                        </h3>
                        <span className="px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold rounded-full">
                          SUBSCRIPTION
                        </span>
                      </div>
                      <p className="text-slate-600 mb-4">
                        Create classrooms, share tests with students, and track their progress in real-time
                      </p>
                      <div className="flex flex-wrap gap-4 justify-center md:justify-start text-sm">
                        <span className="flex items-center gap-1.5 text-slate-700">
                          <School className="w-4 h-4 text-[#4F46E5]" /> Unlimited classrooms
                        </span>
                        <span className="flex items-center gap-1.5 text-slate-700">
                          <Users className="w-4 h-4 text-[#14B8A6]" /> 50 students/class
                        </span>
                        <span className="flex items-center gap-1.5 text-slate-700">
                          <Trophy className="w-4 h-4 text-yellow-500" /> Score tracking
                        </span>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-4xl font-bold text-slate-900 mb-1">$14.99</p>
                      <p className="text-sm text-slate-500 mb-4">/month</p>
                      {subscriptionStatus?.is_premium ? (
                        <Button disabled className="w-full bg-green-600 text-white">
                          <Check className="w-4 h-4 mr-2" />
                          Active
                        </Button>
                      ) : (
                        <Button 
                          onClick={handlePremiumSubscription}
                          disabled={premiumLoading}
                          className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                        >
                          {premiumLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Subscribe
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* How Classrooms Work */}
      <section className="bg-gradient-to-br from-indigo-50 to-purple-50 py-16">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How Classroom Sharing Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Premium teachers can easily share tests with students and track their progress
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6">
              {/* Step 1 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#4F46E5]">1</span>
                </div>
                <h4 className="font-semibold mb-2">Create Classroom</h4>
                <p className="text-sm text-slate-600">Subscribe to Premium and create your first classroom</p>
              </div>

              {/* Step 2 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#4F46E5]">2</span>
                </div>
                <h4 className="font-semibold mb-2">Invite Students</h4>
                <p className="text-sm text-slate-600">Share your unique join code or invite link with students</p>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#4F46E5]">3</span>
                </div>
                <h4 className="font-semibold mb-2">Share Tests</h4>
                <p className="text-sm text-slate-600">Select any test you've created and share it with your classroom</p>
              </div>

              {/* Step 4 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#4F46E5]">4</span>
                </div>
                <h4 className="font-semibold mb-2">Track Progress</h4>
                <p className="text-sm text-slate-600">View scores, leaderboards, and student performance</p>
              </div>
            </div>

            <div className="text-center mt-8">
              <Button onClick={handlePremiumSubscription} className="bg-gradient-to-r from-[#4F46E5] to-purple-600 hover:from-[#4338CA] hover:to-purple-700 text-white gap-2">
                <Crown className="w-4 h-4" />
                Get Premium - $14.99/month
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="container-main text-center text-slate-400">
          <div className="flex justify-center gap-6 mb-4">
            <a href="/user-guide.html" className="hover:text-white transition-colors">User Guide</a>
            <a href="/privacy-policy.html" className="hover:text-white transition-colors">Privacy</a>
            <a href="/terms-of-use.html" className="hover:text-white transition-colors">Terms</a>
          </div>
          <p>© 2026 WebNet KDR. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
